import React, { useState } from 'react';
import { supabase, usuarioDesdeSesion } from '../lib/supabase';
import { User } from '../types';
import { ShieldCheck, Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';

interface AuthProps {
  onLogin: (user: User) => void;
}

export const Auth: React.FC<AuthProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!supabase) {
      setError('El panel no está conectado a Supabase. Revisa VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY.');
      setIsLoading(false);
      return;
    }

    // La contraseña la valida Supabase contra su hash. Nunca viaja en el bundle
    // ni se compara en el navegador.
    const { data, error: errorAuth } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (errorAuth || !data.user) {
      // Mensaje único a propósito: no revelamos si el email existe o no.
      setError('Email o contraseña incorrectos.');
      setIsLoading(false);
      return;
    }

    onLogin(usuarioDesdeSesion(data.user));
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sdai-bg px-4">
      <div className="max-w-md w-full bg-sdai-card rounded-3xl shadow-2xl p-8 sm:p-10 border border-sdai-border">
        
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sdai-500/10 mb-4 border border-sdai-500/20">
            <ShieldCheck className="w-8 h-8 text-sdai-500" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">SDAI Hub</h1>
          <p className="text-gray-500 mt-2 text-sm">
            Acceso restringido al panel visual SDAI.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3.5 bg-red-900/20 border border-red-900/50 text-red-400 text-sm rounded-2xl flex items-center animate-in fade-in">
            <span className="mr-2">⚠️</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1.5 ml-1">
              Email Corporativo
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-3 w-5 h-5 text-gray-500" />
              <input
                type="email"
                id="email"
                required
                className="block w-full pl-11 pr-4 py-2.5 bg-black/20 border border-sdai-border rounded-2xl focus:ring-2 focus:ring-sdai-500 focus:border-sdai-500 transition-colors text-white outline-none"
                placeholder="nombre@sdai.es"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-1.5 ml-1">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-3 w-5 h-5 text-gray-500" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                required
                className="block w-full pl-11 pr-11 py-2.5 bg-black/20 border border-sdai-border rounded-2xl focus:ring-2 focus:ring-sdai-500 focus:border-sdai-500 transition-colors text-white outline-none"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-3 text-gray-500 hover:text-gray-300 focus:outline-none"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3 px-5 rounded-full shadow-lg shadow-sdai-500/20 text-sm font-bold text-black bg-sdai-500 hover:bg-sdai-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sdai-500 disabled:opacity-50 transition-all active:scale-95"
          >
            {isLoading ? "Validando..." : "Iniciar Sesión"}
            {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
          </button>
        </form>

        <div className="mt-6 text-center">
            <p className="text-xs text-gray-600">
                SDAI Visual Hub · solo lectura
            </p>
        </div>
      </div>
    </div>
  );
};
